import { useState, useEffect, useRef } from 'preact/hooks';

interface AnimatedNumberProps {
  /** Target numeric value to display. */
  value: number;
  /** Formats the (interpolated) number into the shown string. */
  format: (n: number) => string;
  /** Animation duration in ms. */
  durationMs?: number;
  /** Optional class on the wrapping span. */
  class?: string;
}

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

// ease-out cubic — fast start, gentle settle
const easeOut = (t: number): number => 1 - Math.pow(1 - t, 3);

/**
 * Counts the displayed number from its previous value to the new target with a
 * compositor-light rAF tween. Honors prefers-reduced-motion (snaps instantly).
 */
export function AnimatedNumber({ value, format, durationMs = 600, class: className }: AnimatedNumberProps) {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const rafRef = useRef<number>();

  useEffect(() => {
    if (value === fromRef.current) return;

    if (prefersReducedMotion()) {
      fromRef.current = value;
      setDisplay(value);
      return;
    }

    const from = fromRef.current;
    const delta = value - from;
    const start = performance.now();

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const current = from + delta * easeOut(t);
      if (t >= 1) {
        fromRef.current = value;
        setDisplay(value);
      } else {
        setDisplay(current);
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [value, durationMs]);

  return <span class={className}>{format(display)}</span>;
}
